/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Users, MapPin, Plus, Trash2, Shield, Save, Briefcase } from 'lucide-react';
import { Client, CostCenter, isClientRole } from '../types.ts';

interface ClientConfigProps {
  clients: Client[];
  costCenters: CostCenter[];
  onAddClient: (client: Client) => void;
  onAddCostCenter: (cc: CostCenter) => void;
  currentUser: { role: string };
}

export default function ClientConfig({
  clients,
  costCenters,
  onAddClient,
  onAddCostCenter,
  currentUser
}: ClientConfigProps) {
  
  const [activeTab, setActiveTab] = useState<'clients' | 'costCenters'>('clients');

  // Client Form State
  const [cCode, setCCode] = useState('');
  const [cName, setCName] = useState('');
  const [cRfc, setCRfc] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cAddress, setCAddress] = useState('');
  const [assignCcOption, setAssignCcOption] = useState<'none' | 'existing' | 'new'>('none');
  const [selectedCcIds, setSelectedCcIds] = useState<string[]>([]);
  const [newCcCode, setNewCcCode] = useState('');
  const [newCcName, setNewCcName] = useState('');
  const [newCcManager, setNewCcManager] = useState('');
  const [newCcAddress, setNewCcAddress] = useState('');

  // Cost Center Form State
  const [ccSelectedClientIds, setCcSelectedClientIds] = useState<string[]>([]);
  const [ccCode, setCcCode] = useState('');
  const [ccName, setCcName] = useState('');
  const [ccManager, setCcManager] = useState('');
  const [ccAddress, setCcAddress] = useState('');
  const [ccLat, setCcLat] = useState<number | string>(25.6866);
  const [ccLng, setCcLng] = useState<number | string>(-100.3161);

  const toggleCcSelectedClient = (clientId: string) => {
    setCcSelectedClientIds(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleClientCcAssignment = (ccId: string) => {
    setSelectedCcIds(prev =>
      prev.includes(ccId)
        ? prev.filter(id => id !== ccId)
        : [...prev, ccId]
    );
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isClientRole(currentUser.role)) return;

    const clientId = `c-${Math.random().toString(36).substr(2, 9)}`;
    const newClient: Client = {
      id: clientId,
      code: cCode.trim().toUpperCase(),
      name: cName.trim(),
      rfc: cRfc.trim().toUpperCase(),
      contactEmail: cEmail.trim(),
      contactPhone: cPhone.trim(),
      address: cAddress.trim(),
      lat: 25.6866 + (Math.random() - 0.5) * 0.1,
      lng: -100.3161 + (Math.random() - 0.5) * 0.1,
      costCenterIds: assignCcOption === 'existing' ? selectedCcIds : []
    };

    onAddClient(newClient);

    // Link Cost Center(s)
    if (assignCcOption === 'existing' && selectedCcIds.length > 0) {
      selectedCcIds.forEach(ccId => {
        const foundCc = costCenters.find(cc => cc.id === ccId);
        if (foundCc) {
          const updatedClientIds = Array.from(new Set([...(foundCc.clientIds || [foundCc.clientId]), clientId]));
          onAddCostCenter({
            ...foundCc,
            clientIds: updatedClientIds
          });
        }
      });
    } else if (assignCcOption === 'new' && newCcCode && newCcName) {
      onAddCostCenter({
        id: `cc-${Math.random().toString(36).substr(2, 9)}`,
        clientId: clientId,
        clientIds: [clientId],
        code: newCcCode.trim().toUpperCase(),
        name: newCcName.trim(),
        manager: newCcManager.trim() || 'Gerente de Enlace',
        address: newCcAddress.trim() || cAddress.trim(),
        lat: 25.6866 + (Math.random() - 0.5) * 0.1,
        lng: -100.3161 + (Math.random() - 0.5) * 0.1
      });
    }
    
    // Reset Form
    setCCode('');
    setCName('');
    setCRfc('');
    setCEmail('');
    setCPhone('');
    setCAddress('');
    setAssignCcOption('none');
    setSelectedCcIds([]);
    setNewCcCode('');
    setNewCcName('');
    setNewCcManager('');
    setNewCcAddress('');
    await window.showAppAlert('Cliente registrado exitosamente en base de datos.', 'Cliente Registrado');
  };

  const handleCreateCostCenter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isClientRole(currentUser.role)) return;

    if (!ccCode.trim() || !ccName.trim()) {
      await window.showAppAlert('Por favor ingrese el código y nombre del centro de costos.', 'Campos Requeridos');
      return;
    }

    const parsedLat = typeof ccLat === 'number' ? ccLat : (parseFloat(String(ccLat)) || 25.6866);
    const parsedLng = typeof ccLng === 'number' ? ccLng : (parseFloat(String(ccLng)) || -100.3161);

    const primaryClientId = ccSelectedClientIds.length > 0 ? ccSelectedClientIds[0] : undefined;

    const newCC: CostCenter = {
      id: `cc-${Math.random().toString(36).substr(2, 9)}`,
      clientId: primaryClientId,
      clientIds: ccSelectedClientIds,
      code: ccCode.trim().toUpperCase(),
      name: ccName.trim(),
      manager: ccManager.trim(),
      address: ccAddress.trim(),
      lat: parsedLat,
      lng: parsedLng,
    };

    onAddCostCenter(newCC);

    const count = ccSelectedClientIds.length;
    // Reset Form
    setCcSelectedClientIds([]);
    setCcCode('');
    setCcName('');
    setCcManager('');
    setCcAddress('');
    setCcLat(25.6866);
    setCcLng(-100.3161);
    if (count > 0) {
      await window.showAppAlert(`Centro de costos vinculado exitosamente con ${count} cliente(s).`, 'Sede Vinculada');
    } else {
      await window.showAppAlert('Centro de costos registrado exitosamente para uso interno (sin clientes mandantes vinculados).', 'Sede Interna Creada');
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Tab Selectors */}
      <div className="flex border-b border-gray-100">
        <button
          onClick={() => setActiveTab('clients')}
          className={`pb-3 text-sm font-semibold border-b-2 px-4 transition-all flex items-center gap-1.5 ${
            activeTab === 'clients' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <Users className="h-4 w-4" /> Directorio de Clientes
        </button>
        <button
          onClick={() => setActiveTab('costCenters')}
          className={`pb-3 text-sm font-semibold border-b-2 px-4 transition-all flex items-center gap-1.5 ${
            activeTab === 'costCenters' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <MapPin className="h-4 w-4" /> Centros de Costos
        </button>
      </div>

      {activeTab === 'clients' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" id="client-directory-panel">
          
          {/* CLIENT LISTING */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Clientes Activos</h3>
            
            <div className="space-y-3">
              {clients.map(client => (
                <div key={client.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-sm uppercase tracking-wider font-mono">
                        {client.code}
                      </span>
                      <h4 className="text-base font-bold text-gray-900 mt-1">{client.name}</h4>
                      <p className="text-xs text-gray-400 font-mono">RFC: {client.rfc}</p>
                    </div>
                    <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-1 rounded-lg">
                      Vinculado
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600 border-t border-gray-50 pt-2.5">
                    <div>
                      <span className="text-gray-400 block uppercase text-[8px] font-bold">Correo Electrónico</span>
                      <p className="font-medium text-gray-800">{client.contactEmail}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 block uppercase text-[8px] font-bold">Teléfono de Enlace (SMS)</span>
                      <p className="font-medium text-gray-800">{client.contactPhone}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-gray-400 block uppercase text-[8px] font-bold">Dirección Fiscal / Principal</span>
                      <p className="font-medium text-gray-800">{client.address}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CLIENT REGISTRATION FORM */}
          {!isClientRole(currentUser.role) ? (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs h-fit space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                <Users className="h-5 w-5 text-blue-600" />
                <h4 className="text-sm font-bold text-gray-900">Registrar Nuevo Cliente</h4>
              </div>

              <form onSubmit={handleCreateClient} className="space-y-3.5">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Código de Cliente (Único)</label>
                  <input
                    type="text"
                    required
                    placeholder="E.g. CLI-TERNIUM"
                    value={cCode}
                    onChange={(e) => setCCode(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Razón Social</label>
                  <input
                    type="text"
                    required
                    placeholder="Nombre legal completo..."
                    value={cName}
                    onChange={(e) => setCName(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">RFC</label>
                  <input
                    type="text"
                    required
                    placeholder="RFC de 12 o 13 dígitos..."
                    value={cRfc}
                    onChange={(e) => setCRfc(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Email de Notificaciones</label>
                  <input
                    type="email"
                    required
                    placeholder="correo@ejemplo.com"
                    value={cEmail}
                    onChange={(e) => setCEmail(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Teléfono Enlace (SMS)</label>
                  <input
                    type="text"
                    required
                    placeholder="+52 811..."
                    value={cPhone}
                    onChange={(e) => setCPhone(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Domicilio Principal</label>
                  <textarea
                    required
                    placeholder="Calle, Número, Colonia, CP..."
                    value={cAddress}
                    onChange={(e) => setCAddress(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 h-16"
                  />
                </div>

                <div className="space-y-2 pt-2 border-t border-gray-100">
                  <label className="text-xs font-bold text-gray-800 block">Vincular Centro de Costos</label>
                  <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setAssignCcOption('none')}
                      className={`py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'none' ? 'bg-slate-900 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200'}`}
                    >
                      Sin Centro
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignCcOption('existing')}
                      className={`py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'existing' ? 'bg-slate-900 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200'}`}
                    >
                      Asignar Existente
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignCcOption('new')}
                      className={`py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'new' ? 'bg-slate-900 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200'}`}
                    >
                      Crear Nuevo
                    </button>
                  </div>

                  {assignCcOption === 'existing' && (
                    <div className="space-y-1.5 bg-gray-50 p-2.5 rounded-xl border border-gray-200">
                      <label className="text-[10px] font-bold text-gray-700 uppercase block">
                        Centros de Costos a Vincular (Seleccione 1 o más)
                      </label>
                      <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                        {costCenters.map(cc => {
                          const isChecked = selectedCcIds.includes(cc.id);
                          return (
                            <label
                              key={cc.id}
                              onClick={() => toggleClientCcAssignment(cc.id)}
                              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-all ${
                                isChecked
                                  ? 'bg-blue-50 border-blue-400 text-blue-900 font-bold'
                                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}} // handled by label onClick
                                className="h-3.5 w-3.5 text-blue-600 rounded cursor-pointer"
                              />
                              <span className="truncate">{cc.name} ({cc.code})</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {assignCcOption === 'new' && (
                    <div className="space-y-2 bg-gray-50 p-2.5 rounded-lg border border-gray-150">
                      <div className="space-y-0.5">
                        <label className="text-[9px] font-bold text-gray-500 uppercase">Código del Centro</label>
                        <input
                          type="text"
                          required
                          placeholder="E.g. CC-MTY-03"
                          value={newCcCode}
                          onChange={(e) => setNewCcCode(e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1 text-xs text-slate-800 font-mono"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-[9px] font-bold text-gray-500 uppercase">Nombre del Centro / Sucursal</label>
                        <input
                          type="text"
                          required
                          placeholder="Almacén Escobedo"
                          value={newCcName}
                          onChange={(e) => setNewCcName(e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1 text-xs text-slate-800"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-[9px] font-bold text-gray-500 uppercase">Gerente de Enlace</label>
                        <input
                          type="text"
                          placeholder="Ing. Alberto Gómez"
                          value={newCcManager}
                          onChange={(e) => setNewCcManager(e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1 text-xs text-slate-800"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl text-xs font-bold transition-all"
                >
                  Registrar Cliente
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-gray-50 p-5 rounded-2xl border border-dashed border-gray-200 text-center text-xs text-gray-400 h-fit space-y-2">
              <Shield className="h-5 w-5 mx-auto text-gray-300" />
              <p>Los perfiles de cliente no pueden añadir o editar razones sociales directas por medidas de seguridad de RFC.</p>
            </div>
          )}

        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" id="cost-centers-panel">
          
          {/* COST CENTERS LIST */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Centros de Costos Vinculados</h3>
            
            <div className="space-y-3">
              {costCenters.map(cc => {
                const assignedClients = clients.filter(c => 
                  c.id === cc.clientId || 
                  c.code === cc.clientId || 
                  (cc.clientIds && (cc.clientIds.includes(c.id) || cc.clientIds.includes(c.code)))
                );
                
                return (
                  <div key={cc.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between gap-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] bg-slate-100 text-slate-700 font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-mono">
                          {cc.code}
                        </span>
                        <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">
                          {assignedClients.length} {assignedClients.length === 1 ? 'Cliente' : 'Clientes'}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900">{cc.name}</h4>
                      
                      {/* Assigned Clients Pills */}
                      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                        <span className="text-[10px] text-gray-400 font-semibold uppercase">Clientes:</span>
                        {assignedClients.map(c => (
                          <span key={c.id} className="text-[10px] bg-slate-100 text-slate-800 font-bold px-2 py-0.5 rounded-md border border-slate-200">
                            🏢 {c.name}
                          </span>
                        ))}
                      </div>

                      <p className="text-[11px] text-gray-500 font-medium">📍 {cc.address}</p>
                    </div>

                    <div className="text-right shrink-0 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 text-xs">
                      <Briefcase className="h-4 w-4 text-gray-400 ml-auto mb-1" />
                      <span className="text-[10px] text-gray-400 block uppercase font-bold">Encargado de Enlace</span>
                      <span className="font-bold text-gray-800">{cc.manager}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* COST CENTER REGISTER FORM */}
          {!isClientRole(currentUser.role) ? (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs h-fit space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                <MapPin className="h-5 w-5 text-blue-600" />
                <h4 className="text-sm font-bold text-gray-900">Vincular Centro de Costos</h4>
              </div>

              <form onSubmit={handleCreateCostCenter} className="space-y-3.5">
                
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                    <span>Clientes Asignados (Opcional)</span>
                    <span className="text-[10px] text-blue-600 font-bold">
                      {ccSelectedClientIds.length === 0 ? 'Sin cliente (Uso Interno)' : `(${ccSelectedClientIds.length} seleccionados)`}
                    </span>
                  </label>
                  
                  <div className="space-y-1 bg-gray-50 p-2.5 rounded-xl border border-gray-200 max-h-40 overflow-y-auto">
                    {clients.map(c => {
                      const isChecked = ccSelectedClientIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          onClick={() => toggleCcSelectedClient(c.id)}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-all ${
                            isChecked
                              ? 'bg-blue-50 border-blue-400 text-blue-900 font-bold'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}} // handled by label onClick
                            className="h-3.5 w-3.5 text-blue-600 rounded cursor-pointer"
                          />
                          <span className="truncate">🏢 {c.name} ({c.code})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Código de Centro (Único)</label>
                  <input
                    type="text"
                    required
                    placeholder="E.g. CC-MTY-FORJA"
                    value={ccCode}
                    onChange={(e) => setCcCode(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Nombre del Centro / Sucursal</label>
                  <input
                    type="text"
                    required
                    placeholder="Planta Principal, Almacén General..."
                    value={ccName}
                    onChange={(e) => setCcName(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Ingeniero Encargado de Enlace</label>
                  <input
                    type="text"
                    required
                    placeholder="Nombre completo del gerente..."
                    value={ccManager}
                    onChange={(e) => setCcManager(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Domicilio de Operación / Recolección</label>
                  <textarea
                    required
                    placeholder="Calle, Número, Zona Industrial, CP..."
                    value={ccAddress}
                    onChange={(e) => setCcAddress(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 h-14"
                  />
                </div>

                {/* Coordenadas GPS Panel */}
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700">🗺️ Coordenadas CEDIS / Mapa</label>
                    <button
                      type="button"
                      onClick={() => {
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (pos) => {
                              setCcLat(Number(pos.coords.latitude.toFixed(6)));
                              setCcLng(Number(pos.coords.longitude.toFixed(6)));
                            },
                            () => alert('No se pudo obtener la ubicación GPS.')
                          );
                        }
                      }}
                      className="text-[10px] font-bold text-blue-600 hover:underline"
                    >
                      📍 Usar Mi GPS
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500">Latitud</label>
                      <input
                        type="number"
                        step="any"
                        required
                        placeholder="25.6866"
                        value={ccLat}
                        onChange={(e) => setCcLat(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500">Longitud</label>
                      <input
                        type="number"
                        step="any"
                        required
                        placeholder="-100.3161"
                        value={ccLng}
                        onChange={(e) => setCcLng(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 pt-1">
                    <button
                      type="button"
                      onClick={() => { setCcLat(25.6866); setCcLng(-100.3161); }}
                      className="text-[9px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-700"
                    >
                      MTY (25.6866, -100.3161)
                    </button>
                    <button
                      type="button"
                      onClick={() => { setCcLat(22.1565); setCcLng(-100.9855); }}
                      className="text-[9px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-700"
                    >
                      SLP (22.1565, -100.9855)
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl text-xs font-bold transition-all"
                >
                  Registrar Centro de Costos
                </button>

              </form>
            </div>
          ) : (
            <div className="bg-gray-50 p-5 rounded-2xl border border-dashed border-gray-200 text-center text-xs text-gray-400 h-fit space-y-2">
              <Shield className="h-5 w-5 mx-auto text-gray-300" />
              <p>Solo el operador interno o administrador puede registrar nuevos centros de costo vinculados.</p>
            </div>
          )}

        </div>
      )}

    </div>
  );
}
